import React, { useEffect, useState } from "react";
import Dialog from "../../../../common/element/Dialog";
import { useDispatch, useSelector } from "react-redux";
import SweetAlertService from "../../../../common/SweetAlert/SweetAlert";

import {
  getAssignmentQuestions,
  submitAssignmentAnswers,
  clearAssignmentQuestions,
  clearSubmitAssignmentAnswers,
  getAssignmentAnswers
} from "../../../../redux/Employee/employeesSlice";

import {
  selectAssignmentQuestions,
  selectAssignmentQuestionsLoading,
  selectSubmitAssignmentAnswersData,
  selectSubmitAssignmentAnswersLoading,
  selectSubmitAssignmentAnswersError,
  selectAssignmentAnswers
} from "../../../../redux/Employee/employeesSelector";

const EmployeeAssignmentDialog = ({
  dialogOpen = false,
  assignment, // This contains the data from OverViewEmployee
  onDialogClose = () => {},
  sessionAssignmentId = null, // This might be used by other pages
  handleRefresh = () => {},
  mode = "edit"
}) => {
  const dispatch = useDispatch();
  const [answers, setAnswers] = useState({});

  // Redux Selectors
  const assignmentData = useSelector(selectAssignmentQuestions);
  const loadingQuestions = useSelector(selectAssignmentQuestionsLoading);
  
  const submitData = useSelector(selectSubmitAssignmentAnswersData);
  const submitting = useSelector(selectSubmitAssignmentAnswersLoading);
  const submitError = useSelector(selectSubmitAssignmentAnswersError);
  const getAssignmentAnswersData = useSelector(selectAssignmentAnswers);

  // ---------------------------------------------------------
  // 1. DETERMINE QUESTIONS SOURCE
  // ---------------------------------------------------------
  const questionsList = (() => {
    if (assignment?.questions?.length > 0) return assignment.questions;
    if (Array.isArray(assignmentData) && assignmentData.length > 0) return assignmentData;
    if (assignmentData?.questions?.length > 0) return assignmentData.questions;
    if (Array.isArray(getAssignmentAnswersData) && getAssignmentAnswersData.length > 0) return getAssignmentAnswersData;
    return [];
  })();

  // ---------------------------------------------------------
  // 2. DETERMINE TITLE SOURCE
  // ---------------------------------------------------------
  const displayTitle = assignment?.assignment_name || 
    (!Array.isArray(assignmentData) ? assignmentData?.assignment_name : "Assignment Questions");

  // Fetch Questions when Dialog Opens
  const AssignmentIds = sessionAssignmentId?.assignment_id;
  const sessionAssignmentIds = sessionAssignmentId?.assignment?.session_assignment_id || assignment?.session_assignment_id;

  useEffect(() => {
    if (dialogOpen && AssignmentIds) {
      dispatch(getAssignmentQuestions({ sessionAssignmentId: AssignmentIds }));
      dispatch(getAssignmentAnswers({ sessionAssignmentId: sessionAssignmentIds }));
    }
    else if (dialogOpen && sessionAssignmentIds) {
      dispatch(getAssignmentAnswers({ sessionAssignmentId: sessionAssignmentIds }));
    }
   
    return () => {
      if(!dialogOpen) {
          dispatch(clearAssignmentQuestions());
          setAnswers({});
      }
    };
  }, [dialogOpen, sessionAssignmentIds, AssignmentIds, dispatch]);

  // ✅ Populate Answers
  useEffect(() => {
    if (questionsList && questionsList.length > 0) {
      const initialAnswers = {};

      questionsList.forEach(q => {
        const matchingData = Array.isArray(getAssignmentAnswersData)
          ? getAssignmentAnswersData.find(a => a.question_id === q.question_id)
          : null;

        if (q.question_type === "with_sub" && q.sub_questions?.length > 0) {
          q.sub_questions.forEach((_, subIndex) => {
            const subKey = `${q.question_id}_sub_${subIndex}`;
            let subAnswerText = "";
            if (matchingData?.answers) {
              const subAnswer = matchingData.answers.find(a => a.sub_question_index === subIndex);
              subAnswerText = subAnswer?.answer_text || "";
            }
            initialAnswers[subKey] = subAnswerText;
          });
        } else {
          let answerText = "";
          if (matchingData?.answers) {
            answerText = matchingData.answers[0]?.answer_text || "";
          } else if (matchingData) {
            answerText = matchingData.answer_text || q.answer || "";
          } else {
            answerText = q.answer || "";
          }
          initialAnswers[q.question_id] = answerText;
        }
      });

      setAnswers(initialAnswers);
    }
  }, [questionsList, getAssignmentAnswersData]);

  // Handle Success/Error for Submission
  useEffect(() => {
    if (submitData) {
      SweetAlertService.showSuccess({
        title: "Success",
        message: submitData?.message || "Assignment submitted successfully!",
        onConfirm: () => {
          dispatch(clearSubmitAssignmentAnswers());
          onDialogClose();
          handleRefresh();
        }
      });
    }
  }, [submitData, dispatch, onDialogClose, handleRefresh]);

  useEffect(() => {
    if (submitError) {
      SweetAlertService.showError({
        title: "Error",
        message: typeof submitError === 'string' ? submitError : "Failed to submit assignment.",
      });
      dispatch(clearSubmitAssignmentAnswers());
    }
  }, [submitError, dispatch]);

  const handleAnswerChange = (questionId, value) => {
    if(mode === 'view') return;
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const buildFormattedAnswers = () => {
    return Object.entries(answers)
      .filter(([, text]) => text && text.trim() !== "")
      .map(([key, text]) => {
        if (key.includes("_sub_")) {
          const parts = key.split("_sub_");
          const questionId = parseInt(parts[0]);
          const subIndex = parseInt(parts[1]);
          return { question_id: questionId, sub_question_index: subIndex, answer_text: text };
        }
        return { question_id: parseInt(key), answer_text: text };
      });
  };

  const handleSubmitInternal = () => {
    const finalSessionAssignmentId = assignment?.session_assignment_id || sessionAssignmentId?.assignment?.session_assignment_id;

    if (!finalSessionAssignmentId) {
      console.error("No Session Assignment ID found");
      return;
    }

    const formattedAnswers = buildFormattedAnswers();

    if(formattedAnswers.length === 0) {
       SweetAlertService.showError({
        title: "Warning",
        message: "Please answer at least one question.",
      });
      return;
    }

    const payload = {
      session_assignment_id: finalSessionAssignmentId,
      answers: formattedAnswers
    };

    dispatch(submitAssignmentAnswers({ data: payload }));
  };

  const handleSaveAsDraft = () => {
    const finalSessionAssignmentId = assignment?.session_assignment_id || sessionAssignmentId?.assignment?.session_assignment_id;

    if (!finalSessionAssignmentId) {
      console.error("No Session Assignment ID found");
      return;
    }

    const formattedAnswers = buildFormattedAnswers();

    const payload = {
      session_assignment_id: finalSessionAssignmentId,
      is_draft: true,
      answers: formattedAnswers
    };

    dispatch(submitAssignmentAnswers({ data: payload }));
  };

  if (!dialogOpen) return null;

  return (
    <Dialog
      isOpen={dialogOpen}
      onClose={onDialogClose}
      width={900}
    >
      {/* Header */}
      <div className="flex border-b px-6 py-4 items-center justify-between">
        <div className="flex items-center">
          <span className="h-10 w-10 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-3">
            <svg width="22" height="20" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11 19L10.8999 18.8499C10.2053 17.808 9.85798 17.287 9.3991 16.9098C8.99286 16.5759 8.52476 16.3254 8.02161 16.1726C7.45325 16 6.82711 16 5.57482 16H4.2C3.07989 16 2.51984 16 2.09202 15.782C1.71569 15.5903 1.40973 15.2843 1.21799 14.908C1 14.4802 1 13.9201 1 12.8V4.2C1 3.07989 1 2.51984 1.21799 2.09202C1.40973 1.71569 1.71569 1.40973 2.09202 1.21799C2.51984 1 3.07989 1 4.2 1H4.6C6.84021 1 7.96031 1 8.81596 1.43597C9.56861 1.81947 10.1805 2.43139 10.564 3.18404C11 4.03968 11 5.15979 11 7.4M11 19V7.4M11 19L11.1001 18.8499C11.7947 17.808 12.142 17.287 12.6009 16.9098C13.0071 16.5759 13.4752 16.3254 13.9784 16.1726C14.5467 16 15.1729 16 16.4252 16H17.8C18.9201 16 19.4802 16 19.908 15.782C20.2843 15.5903 20.5903 15.2843 20.782 14.908C21 14.4802 21 13.9201 21 12.8V4.2C21 3.07989 21 2.51984 20.782 2.09202C20.5903 1.71569 20.2843 1.40973 19.908 1.21799C19.4802 1 18.9201 1 17.8 1H17.4C15.1598 1 14.0397 1 13.184 1.43597C12.4314 1.81947 11.8195 2.43139 11.436 3.18404C11 4.03968 11 5.15979 11 7.4" stroke="#344054" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <h5 className="mb-0 text-gray-900 text-lg font-semibold">
            {mode === 'view' ? "View Assignment" : "Start Assignment"}
          </h5>
        </div>
        <button onClick={onDialogClose} className="text-gray-400 hover:text-gray-600">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* Content Area */}
      <div className="overflow-y-auto px-6 pb-6 text-start" style={{ maxHeight: '450px' }}>
        
        {loadingQuestions && (!questionsList || questionsList.length === 0) ? (
          <div className="py-10 text-center text-gray-500">Loading questions...</div>
        ) : (
          <>
            <div className="mb-6">
              <h2 className="text-base font-semibold text-gray-900">
                 {displayTitle}
              </h2>
            </div>

            <div className="space-y-5">
              {questionsList.length > 0 ? (
                questionsList.map((question, index) => {
                  const questionNumber = index + 1;
                  const questionId = question.question_id;

                  const isWithSub = question.question_type === "with_sub" && question.sub_questions?.length > 0;

                  return (
                    <div key={questionId} className="border-b border-gray-100 pb-5 last:border-b-0">
                      {/* Main Question */}
                      <p className="text-gray-900 text-sm leading-5 mb-3">
                        <span className="font-medium">Q{questionNumber}.</span> {question.question_text}
                      </p>

                      {isWithSub ? (
                        /* Sub-questions each with their own answer textarea */
                        <div className="space-y-3 ml-4">
                          {question.sub_questions.map((sub, subIndex) => {
                            const subKey = `${questionId}_sub_${subIndex}`;
                            return (
                              <div key={subIndex}>
                                <p className="text-gray-700 text-sm leading-5 mb-1">
                                  <span className="font-medium text-gray-500">
                                    {String.fromCharCode(97 + subIndex)}.
                                  </span>{" "}
                                  {sub}
                                </p>
                                <textarea
                                  className={`pl-3 pr-3 py-2 w-full input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline resize-none overflow-hidden ${mode === 'view' ? 'bg-gray-50' : ''}`}
                                  value={answers[subKey] || ""}
                                  placeholder={mode === 'view' ? "No answer provided" : "Type your answer here..."}
                                  disabled={mode === 'view'}
                                  ref={(el) => {
                                    if (el) {
                                      el.style.height = 'auto';
                                      el.style.height = el.scrollHeight + 'px';
                                    }
                                  }}
                                  onChange={(e) => {
                                    handleAnswerChange(subKey, e.target.value);
                                    e.target.style.height = 'auto';
                                    e.target.style.height = e.target.scrollHeight + 'px';
                                  }}
                                  rows={1}
                                />
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        /* Simple question — single answer textarea */
                        <textarea
                          className={`pl-3 pr-3 py-2 w-full input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline resize-none overflow-hidden ${mode === 'view' ? 'bg-gray-50' : ''}`}
                          value={answers[questionId] || ""}
                          placeholder={mode === 'view' ? "No answer provided" : "Type your answer here..."}
                          disabled={mode === 'view'}
                          ref={(el) => {
                            if (el) {
                              el.style.height = 'auto';
                              el.style.height = el.scrollHeight + 'px';
                            }
                          }}
                          onChange={(e) => {
                            handleAnswerChange(questionId, e.target.value);
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                          }}
                          rows={1}
                        />
                      )}
                    </div>
                  );
                })
              ) : (
                 <p className="text-sm text-gray-500">No questions available for this assignment.</p>
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer Buttons */}
      <div className="flex border-t px-6 py-4 justify-end gap-3 mt-auto">
        <button
          className="px-6 py-2 rounded-lg text-sm font-medium transition-colors"
          onClick={mode === 'view' ? onDialogClose : handleSaveAsDraft}
          style={{ backgroundColor: "#FFF4E5" }}
          disabled={submitting}
        >
          {mode === 'view' ? "Close" : "Save as Draft"}
        </button>
        
        {mode !== 'view' && (
          <button
            onClick={handleSubmitInternal}
            disabled={submitting || (loadingQuestions && questionsList.length === 0)}
            className="px-6 py-2 rounded-lg text-sm font-medium text-white transition-colors hover:opacity-90 flex items-center justify-center"
            style={{
              backgroundColor: "#FF8E1C"
            }}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        )}
      </div>
    </Dialog>
  );
};

export default EmployeeAssignmentDialog;